// Porta JS de `src/project/disbursement/engine.js` do back-end. Mesmo algoritmo,
// mesmas invariantes, mesmas fixtures. Só é usada para a pré-visualização do
// editor — a curva publicada continua sendo a do back-end.
//
// Regras de centavos: TODO peso passado para `allocateProportional` é inteiro
// não-negativo (centavos de composição ou centavos mensais) — nunca fração.

import { allocateCents, allocateProportional } from "./allocate";
import { validateRuleBasis, validateRuleProfile, validateStartMonth } from "./profile";
import {
  ancestorsOf, compareMonthKey, levelOf, parseMonthKey, rollUpSeries, shiftMonth,
} from "./hierarchy";
import { advanceBusinessDays, monthKeyOfSerial, referenceDayOf } from "./calendar";

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;
const MAX_PROVENANCE_KEYS = 20;

export class EngineError extends Error {
  constructor(errorCode, message, details = null) {
    super(message);
    this.name = "EngineError";
    this.errorCode = errorCode;
    this.details = details;
  }
}

export const normalizeText = (value) => String(value ?? "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase()
  .trim();

export const ruleKeys = Object.freeze({
  resource: (id) => `resource:${String(id)}`,
  ampliGroup: (groupId) => `resource_group:ampli:${String(groupId)}`,
  erpGroup: (name) => `resource_group:erp:${normalizeText(name)}`,
  node: (wbs) => `node:${String(wbs)}`,
});

const sumMap = (map) => {
  let total = 0;
  for (const value of map.values()) total += value;
  return total;
};
const addInto = (map, key, cents) => {
  if (cents !== 0) map.set(key, (map.get(key) ?? 0) + cents);
};
const sorted = (map) => new Map(
  [...map].filter(([, value]) => value !== 0).sort(([a], [b]) => compareMonthKey(a, b)),
);
const isCents = (value) => Number.isSafeInteger(value);

const monthIndex = ({ year, month }) => year * 12 + (month - 1);

// Resolve a defasagem em MESES de uma linha do perfil para `sourceMonth` (design D3). Uma
// linha em meses (legada ou explícita) é o próprio offset, sem cálculo. Uma linha em dias
// passa por calendar.js: a data de referência é o ÚLTIMO dia de `sourceMonth` (design D4),
// deslocada pelos dias corridos ou úteis da linha, e o offset é a distância em meses de
// `sourceMonth` até a competência em que a data deslocada cai.
const resolveLineMonthOffset = (line, sourceMonth) => {
  if (line.unit === undefined || line.unit === "months") return line.offsetMonths;
  const referenceSerial = referenceDayOf(sourceMonth);
  const destinationSerial = line.unit === "calendar_days"
    ? referenceSerial + line.offset
    : advanceBusinessDays(referenceSerial, line.offset);
  const destinationMonth = monthKeyOfSerial(destinationSerial);
  return monthIndex(parseMonthKey(destinationMonth)) - monthIndex(parseMonthKey(sourceMonth));
};

// Memoizado por (perfil, mês de origem) e criado UMA vez por rodada (nunca em escopo de
// módulo, para não acumular perfis de obras/edições já fechadas na mesma aba): dentro de
// uma pré-visualização, várias fatias e vários meses reaproveitam o mesmo perfil
// resolvido, sem recalcular a mesma conversão de calendário por fatia.
const createMonthOffsetsResolver = () => {
  const cache = new Map();
  return (profile, sourceMonth) => {
    let byMonth = cache.get(profile);
    if (!byMonth) {
      byMonth = new Map();
      cache.set(profile, byMonth);
    }
    let offsets = byMonth.get(sourceMonth);
    if (!offsets) {
      offsets = profile.map((line) => resolveLineMonthOffset(line, sourceMonth));
      byMonth.set(sourceMonth, offsets);
    }
    return offsets;
  };
};

// `parseMonthKey` e a autoridade sobre a forma da competencia; aqui o RangeError
// dele vira um `EngineError` tipado para que o chamador trate um unico tipo.
const assertMonthKey = (month, message, details = null) => {
  try {
    parseMonthKey(month);
  } catch (error) {
    throw new EngineError("invalid_input", message, details);
  }
};

const validateInput = (input) => {
  if (!input || typeof input !== "object") throw new EngineError("invalid_input", "engine input required");
  if (typeof input.root !== "string" || !input.root) throw new EngineError("invalid_input", "root required");
  if (!MONTH.test(String(input.currentMonth))) throw new EngineError("invalid_input", "currentMonth must be YYYY-MM");
  if (!(input.plannedLeaves instanceof Map)) throw new EngineError("invalid_input", "plannedLeaves must be a Map");
  if (input.reference !== undefined && input.reference !== null) {
    const { cents, leaves } = input.reference;
    if (!isCents(cents) || cents < 0 || !Number.isSafeInteger(leaves) || leaves < 0) {
      throw new EngineError("invalid_input", "reference must carry non-negative integer cents and leaves");
    }
  }
  if (input.unscheduledLeaves !== undefined && input.unscheduledLeaves !== null) {
    if (!(input.unscheduledLeaves instanceof Map)) {
      throw new EngineError("invalid_input", "unscheduledLeaves must be a Map");
    }
    for (const [wbs, cents] of input.unscheduledLeaves) {
      if (levelOf(wbs, input.root) !== "service") {
        throw new EngineError("leaf_not_service", `unscheduled leaf is not a service: ${wbs}`);
      }
      if (!isCents(cents) || cents < 0) {
        throw new EngineError("negative_planned", `negative or invalid unscheduled budget on ${wbs}`);
      }
    }
  }
  for (const [wbs, series] of input.plannedLeaves) {
    if (levelOf(wbs, input.root) !== "service") {
      throw new EngineError("leaf_not_service", `planned leaf is not a service: ${wbs}`);
    }
    if (!(series instanceof Map)) {
      throw new EngineError("invalid_input", `planned series for ${wbs} must be a Map`);
    }
    for (const [month, cents] of series) {
      if (!MONTH.test(month)) throw new EngineError("invalid_input", `invalid month ${month} on ${wbs}`);
      if (!isCents(cents) || cents < 0) {
        throw new EngineError("negative_planned", `negative or invalid planned on ${wbs}/${month}`);
      }
    }
  }
};

const indexRules = (rules) => {
  const index = new Map();
  for (const rule of rules ?? []) {
    if (rule?.active !== true || typeof rule?.target?.key !== "string") continue;
    const basis = validateRuleBasis(rule.basis);
    // `null` só na base da referência: a regra desloca a origem até o início (D21).
    const profile = validateRuleProfile(rule.profile, basis);
    index.set(rule.target.key, {
      ruleId: rule.ruleId,
      key: rule.target.key,
      basis,
      profile,
      // os pesos de um perfil por peso (D21), uma vez por regra; `null` nos outros
      weights: profile !== null && profile[0].weight !== undefined ? profile.map((line) => line.weight) : null,
      startMonth: validateStartMonth(rule.startMonth),
    });
  }
  return index;
};

// Cada grupo e compilado UMA vez: needle ja normalizada e ids explicitos num Set.
// Sem isso o casamento renormalizava a agulha por linha de composicao x grupo.
// Ordenacao por comparacao CRUA do nome (nao `localeCompare`, que depende de ICU e
// nao daria o mesmo vencedor no back-end), desempate pelo `groupId`.
const compileGroups = (groups) => [...(groups ?? [])]
  .map((group) => {
    const criterion = group?.criterion ?? {};
    return {
      groupId: String(group?.groupId),
      name: String(group?.name),
      kind: criterion.kind,
      needle: criterion.kind === "description_contains" ? normalizeText(criterion.value) : "",
      explicitIds: criterion.kind === "explicit"
        ? new Set((criterion.resourceIds ?? []).map(String))
        : null,
    };
  })
  .sort((left, right) => {
    if (left.name < right.name) return -1;
    if (left.name > right.name) return 1;
    if (left.groupId < right.groupId) return -1;
    if (left.groupId > right.groupId) return 1;
    return 0;
  });

// A pertinencia e do INSUMO, nao da linha de composicao: basta uma das descricoes
// vistas para aquele `resourceId` conter a agulha.
const groupMatches = (group, resourceId, normalizedDescriptions) => {
  if (group.kind === "explicit") return group.explicitIds.has(resourceId);
  if (group.kind === "description_contains") {
    if (group.needle.length === 0) return false;
    for (const description of normalizedDescriptions) {
      if (description.includes(group.needle)) return true;
    }
    return false;
  }
  return false;
};

// Grupos dinamicos sao reavaliados a cada execucao contra a composicao do momento:
// um insumo novo entra no grupo sem migracao nem mudanca de regra.
const indexAmpliGroups = (groups, composition) => {
  const ordered = compileGroups(groups);
  const byResource = new Map();
  if (ordered.length === 0) return byResource;
  // uma passada pela composicao junta as descricoes distintas por insumo
  const descriptionsById = new Map();
  for (const row of composition ?? []) {
    if (row?.resourceId == null) continue;
    const id = String(row.resourceId);
    let descriptions = descriptionsById.get(id);
    if (!descriptions) {
      descriptions = new Set();
      descriptionsById.set(id, descriptions);
    }
    descriptions.add(normalizeText(row.description));
  }
  // o negativo tambem e cacheado (`null`): cada insumo e resolvido uma unica vez
  for (const [id, descriptions] of descriptionsById) {
    const group = ordered.find((candidate) => groupMatches(candidate, id, descriptions));
    byResource.set(id, group ? group.groupId : null);
  }
  return byResource;
};

// Precedencia insumo > grupo Ampli > grupo ERP > servico > ancestrais. As chaves
// de no (`nodeKeys`) sao montadas UMA vez por folha e a busca para no primeiro
// acerto - nada de materializar a lista completa de candidatos por fatia.
const nodeKeysFor = (serviceWbs, root) => [
  ruleKeys.node(serviceWbs),
  ...ancestorsOf(serviceWbs, root).map((ancestor) => ruleKeys.node(ancestor)),
];

// Indice folha-por-no: para cada folha, ela mesma e todos os ancestrais viram
// baldes que apontam de volta pra folha. Montado UMA vez por execucao, usado
// pelo ajuste de mes pra achar os alvos sob um no sem varrer `leafData` inteiro
// por ajuste (`startsWith`).
const indexLeavesByNode = (leafWbsList, root) => {
  const index = new Map();
  for (const wbs of leafWbsList) {
    for (const node of [wbs, ...ancestorsOf(wbs, root)]) {
      let bucket = index.get(node);
      if (!bucket) {
        bucket = [];
        index.set(node, bucket);
      }
      bucket.push(wbs);
    }
  }
  return index;
};

const resolveRule = ({ unit, nodeKeys, ruleIndex, ampliGroupByResource }) => {
  if (unit.resourceId != null) {
    const direct = ruleIndex.get(ruleKeys.resource(unit.resourceId));
    if (direct) return direct;
    const groupId = ampliGroupByResource.get(unit.resourceId);
    if (groupId) {
      const byAmpliGroup = ruleIndex.get(ruleKeys.ampliGroup(groupId));
      if (byAmpliGroup) return byAmpliGroup;
    }
  }
  // O grupo ERP vem da própria linha de composição (`unit.erpGroup`), não do
  // insumo, então independe de `resourceId` estar presente.
  if (unit.erpGroup) {
    const byErpGroup = ruleIndex.get(ruleKeys.erpGroup(unit.erpGroup));
    if (byErpGroup) return byErpGroup;
  }
  for (const key of nodeKeys) {
    const rule = ruleIndex.get(key);
    if (rule) return rule;
  }
  return null;
};

// Pesos = centavos totais da composição (inteiros). Serviço de preço informado
// (sem composição positiva) recebe uma única fatia sintética de peso 1.
// `totalCents` AUSENTE (null/undefined) e tolerado - a linha simplesmente nao pesa.
// Presente mas malformado (fracao, string, negativo, NaN, Infinity) e erro de
// entrada: pesar com isso perderia centavos silenciosamente.
const shareUnits = (rows, serviceWbs) => {
  const positive = [];
  for (const row of rows ?? []) {
    const total = row?.totalCents;
    if (total === null || total === undefined) continue;
    if (!isCents(total) || total < 0) {
      throw new EngineError(
        "invalid_input",
        `invalid composition totalCents on ${serviceWbs}`,
        { serviceWbs, resourceId: row?.resourceId ?? null, totalCents: total },
      );
    }
    if (total > 0) positive.push(row);
  }
  if (positive.length === 0) return [{ resourceId: null, erpGroup: null, weight: 1 }];
  // linha com valor mas sem id: `resourceId` fica null (NUNCA a string "null", que
  // casaria a chave `resource:null` de uma regra)
  return positive.map((row) => ({
    resourceId: row.resourceId == null ? null : String(row.resourceId),
    erpGroup: row.erpGroup ?? null,
    weight: row.totalCents,
  }));
};

// A regra é um cronograma NOVO para o que ela governa, não a quebra dos meses da origem
// (revisão de 2026-10-03): o que a origem pôs em TODOS os meses de cada fatia governada é
// somado e esse total é redistribuído pela forma da regra a partir da âncora — o
// `startMonth` da regra ou, sem ele, o primeiro mês em que a regra governa valor positivo
// na obra. Fatia sem regra fica no mês da origem. Duas passadas, como no back-end: a
// primeira soma (e acha as âncoras), a segunda posiciona.
//
// A regra com base na referência (`basis: "reference"`, D21) sem perfil é a exceção: ela
// DESLOCA os meses da origem do que governa por `startMonth - âncora`, centavo a centavo,
// então a primeira passada guarda a série por mês dessas fatias (e só delas). Com perfil
// por peso, o total é repartido pelos pesos em vez de bps.
const redistributeLeaves = (input, ruleIndex, ampliGroupByResource) => {
  const monthOffsetsOf = createMonthOffsetsResolver();
  const compositionByService = new Map();
  for (const row of input.composition ?? []) {
    if (!compositionByService.has(row.serviceWbs)) compositionByService.set(row.serviceWbs, []);
    compositionByService.get(row.serviceWbs).push(row);
  }
  const firstGoverned = new Map();
  const ruleHits = new Map();
  const pending = [];
  for (const [serviceWbs, series] of input.plannedLeaves) {
    const nodeKeys = nodeKeysFor(serviceWbs, input.root);
    const units = shareUnits(compositionByService.get(serviceWbs), serviceWbs).map((unit) => ({
      ...unit,
      rule: resolveRule({ unit, nodeKeys, ruleIndex, ampliGroupByResource }),
    }));
    const weights = units.map((unit) => unit.weight);
    const totals = units.map(() => 0);
    // série por mês só das fatias que uma regra de deslocamento vence (D21); `null` nas outras
    const shifted = units.map((unit) => (unit.rule !== null && unit.rule.profile === null ? new Map() : null));
    const out = new Map();
    let ruledForLeaf = 0;
    const usedKeys = new Set();
    for (const [month, cents] of series) {
      const parts = units.length === 1 ? [cents] : allocateProportional(cents, weights);
      units.forEach((unit, index) => {
        const part = parts[index];
        if (unit.rule === null) {
          addInto(out, month, part);
          return;
        }
        totals[index] += part;
        if (shifted[index] !== null && part !== 0) addInto(shifted[index], month, part);
        // só um acerto POSITIVO tira a regra de órfã, entra na proveniência e ancora:
        // uma regra sobre previsto zerado não "alcança" nada.
        if (part > 0) {
          ruledForLeaf += part;
          ruleHits.set(unit.rule.key, (ruleHits.get(unit.rule.key) ?? 0) + part);
          usedKeys.add(unit.rule.key);
          const first = firstGoverned.get(unit.rule.key);
          if (first === undefined || compareMonthKey(month, first) < 0) firstGoverned.set(unit.rule.key, month);
        }
      });
    }
    pending.push({ serviceWbs, units, totals, shifted, out, plannedTotal: sumMap(series), ruledForLeaf, usedKeys });
  }
  const leafData = new Map();
  const leafInfo = new Map();
  for (const { serviceWbs, units, totals, shifted, out, plannedTotal, ruledForLeaf, usedKeys } of pending) {
    units.forEach(({ rule }, index) => {
      const total = totals[index];
      if (rule === null || total === 0) return;
      if (shifted[index] !== null) {
        // D21: todo mês da origem anda a mesma distância, do primeiro mês governado até o início
        const first = firstGoverned.get(rule.key);
        const distance = rule.startMonth === null || first === undefined
          ? 0
          : monthIndex(parseMonthKey(rule.startMonth)) - monthIndex(parseMonthKey(first));
        for (const [month, cents] of shifted[index]) addInto(out, shiftMonth(month, distance), cents);
        return;
      }
      const anchor = rule.startMonth ?? firstGoverned.get(rule.key);
      const { profile } = rule;
      const offsets = monthOffsetsOf(profile, anchor);
      // perfil de uma linha recebe 100%: a repartição é o próprio total; perfil por peso
      // (D21) reparte pelos pesos, com o mesmo maior resto
      const pieces = profile.length === 1
        ? [total]
        : rule.weights !== null
          ? allocateProportional(total, rule.weights)
          : allocateCents(total, profile.map((line) => line.percentBps));
      profile.forEach((line, lineIndex) => {
        addInto(out, shiftMonth(anchor, offsets[lineIndex]), pieces[lineIndex]);
      });
    });
    leafData.set(serviceWbs, sorted(out));
    leafInfo.set(serviceWbs, {
      plannedTotal,
      ruledForLeaf,
      share: ruledForLeaf === 0 ? "none" : ruledForLeaf === plannedTotal ? "full" : "partial",
      ruleKeys: [...usedKeys].sort(),
    });
  }
  return { leafData, leafInfo, ruleHits };
};

// Ajuste de mês: move o disponível da competência de origem e marca o excedente.
// O excedente não é movido nem descartado — vira `exceeds_origin` no nó do
// ajuste (e nos ancestrais) além do `exceedsCents` em `adjustmentsStatus`.
const applyAdjustments = (leafData, adjustments, leavesByNode, root) => {
  const status = [];
  const exceedsNodes = new Set();
  const touched = new Set();
  // A capacidade da competencia de origem e consumida em cadeia, entao a ORDEM
  // decide o resultado. Ordem total: `String(wbsCodeId)` ascendente, depois
  // `String(entryId ?? "")` ascendente (ausente ordena primeiro como string
  // vazia), depois a ordem do array — sem isso a comparacao deixa de ser
  // transitiva quando alguns ajustes do mesmo no nao tem entryId.
  const active = (adjustments ?? [])
    .filter((adjustment) => adjustment?.active === true)
    .map((adjustment, index) => ({
      adjustment,
      index,
      wbs: String(adjustment.wbsCodeId),
      entryId: adjustment.entryId == null ? "" : String(adjustment.entryId),
    }))
    .sort((left, right) => {
      if (left.wbs < right.wbs) return -1;
      if (left.wbs > right.wbs) return 1;
      if (left.entryId < right.entryId) return -1;
      if (left.entryId > right.entryId) return 1;
      return left.index - right.index;
    });
  for (const { adjustment, wbs: prefix } of active) {
    if (
      !isCents(adjustment.amountCents) || adjustment.amountCents <= 0
      || !MONTH.test(adjustment.fromCompetence) || !MONTH.test(adjustment.toCompetence)
    ) {
      throw new EngineError("invalid_input", `invalid adjustment ${adjustment?.entryId}`);
    }
    // o ajuste tem de nomear um no real: sob a raiz e numa fronteira de nivel
    if (levelOf(prefix, root) === null) {
      throw new EngineError(
        "invalid_input",
        `adjustment wbsCodeId is not a node under ${root}: ${prefix}`,
        { entryId: adjustment.entryId ?? null, wbsCodeId: prefix },
      );
    }
    const targets = (leavesByNode.get(prefix) ?? [])
      .map((wbs) => ({ wbs, series: leafData.get(wbs) }))
      .filter(({ series }) => (series?.get(adjustment.fromCompetence) ?? 0) > 0);
    const capacity = targets.reduce(
      (total, { series }) => total + series.get(adjustment.fromCompetence), 0,
    );
    const applied = Math.min(adjustment.amountCents, capacity);
    if (applied > 0) {
      // pesos = centavos disponíveis na competência de origem (inteiros)
      const parts = allocateProportional(
        applied,
        targets.map(({ series }) => series.get(adjustment.fromCompetence)),
      );
      targets.forEach(({ wbs, series }, index) => {
        // A série é mutada in-place e re-canonicalizada UMA vez após a fase
        // inteira, então N ajustes no mesmo alvo não reconstroem a folha N
        // vezes. Meses zerados sobrevivem até lá — inofensivo, pois o filtro
        // de capacidade é estritamente `> 0` e o laço só lê por chave.
        addInto(series, adjustment.fromCompetence, -parts[index]);
        addInto(series, adjustment.toCompetence, parts[index]);
        touched.add(wbs);
      });
    }
    const exceedsCents = adjustment.amountCents - applied;
    if (exceedsCents > 0) exceedsNodes.add(prefix);
    status.push({
      entryId: adjustment.entryId,
      wbsCodeId: prefix,
      appliedCents: applied,
      exceedsCents,
    });
  }
  for (const wbs of touched) leafData.set(wbs, sorted(leafData.get(wbs)));
  return { status, exceedsNodes };
};

// So folhas de servico dentro da raiz autorizada entram no netting. Comprometido
// fora do escopo e ignorado, contado em folhas e somado em centavos - nunca cria
// no orfao (o `rollUpSeries` recusaria) nem entra na conciliacao de caixa.
const scopeCommitted = (committed, root) => {
  const inScope = new Map();
  let committedOutsideScope = 0;
  let committedOutsideScopeCents = 0;
  for (const [wbs, entry] of committed ?? new Map()) {
    const paid = entry?.paid instanceof Map ? entry.paid : new Map();
    const toPay = entry?.toPay instanceof Map ? entry.toPay : new Map();
    for (const series of [paid, toPay]) {
      for (const [month, cents] of series) {
        assertMonthKey(month, `invalid committed month ${month} on ${wbs}`, { wbs, month });
        if (!isCents(cents) || cents < 0) {
          throw new EngineError("invalid_input", `invalid committed cents on ${wbs}/${month}`, {
            wbs, month, cents,
          });
        }
      }
    }
    if (levelOf(wbs, root) !== "service") {
      committedOutsideScope += 1;
      committedOutsideScopeCents += sumMap(paid) + sumMap(toPay);
      continue;
    }
    inScope.set(wbs, { paid, toPay });
  }
  return { inScope, committedOutsideScope, committedOutsideScopeCents };
};

const netLeaves = (leafData, committedInScope, currentMonth) => {
  const remainingLeaves = new Map();
  const cashLeaves = new Map();
  const flagsByLeaf = new Map();
  const committedByLeaf = new Map();
  const leaves = new Set([...leafData.keys(), ...committedInScope.keys()]);
  for (const wbs of leaves) {
    const data = leafData.get(wbs) ?? new Map();
    const entry = committedInScope.get(wbs);
    const paid = entry?.paid ?? new Map();
    const toPay = entry?.toPay ?? new Map();
    const paidCents = sumMap(paid);
    const toPayCents = sumMap(toPay);
    // TODA folha de servico em escopo tem entrada, zerada quando nao ha comprometido
    committedByLeaf.set(wbs, { paidCents, toPayCents });
    const plannedTotal = sumMap(data);
    const committedTotal = paidCents + toPayCents;
    const remainingTotal = Math.max(0, plannedTotal - committedTotal);
    const flags = [];
    if (committedTotal > plannedTotal) flags.push("over_planned");
    const remaining = new Map();
    const future = [...data].filter(
      ([month, cents]) => compareMonthKey(month, currentMonth) >= 0 && cents > 0,
    );
    if (remainingTotal > 0 && future.length > 0) {
      // pesos = centavos da curva futura (inteiros)
      const parts = allocateProportional(remainingTotal, future.map(([, cents]) => cents));
      future.forEach(([month], index) => addInto(remaining, month, parts[index]));
    } else if (remainingTotal > 0) {
      remaining.set(currentMonth, remainingTotal);
      flags.push("remaining_without_schedule");
    }
    const cash = new Map();
    for (const series of [paid, toPay, remaining]) {
      for (const [month, cents] of series) addInto(cash, month, cents);
    }
    remainingLeaves.set(wbs, sorted(remaining));
    cashLeaves.set(wbs, sorted(cash));
    flagsByLeaf.set(wbs, flags);
  }
  return { remainingLeaves, cashLeaves, flagsByLeaf, committedByLeaf };
};

const verifyInvariants = ({
  input, leafData, rolledData, rolledCash, rolledRemaining, committedByLeaf,
}) => {
  const plannedRoot = [...input.plannedLeaves.values()]
    .reduce((total, series) => total + sumMap(series), 0);
  const dataRoot = sumMap(rolledData.get(input.root) ?? new Map());
  if (dataRoot !== plannedRoot) {
    throw new EngineError("invariant_root_total", "root curve does not match planned", { dataRoot, plannedRoot });
  }
  for (const [wbs, series] of input.plannedLeaves) {
    if (sumMap(leafData.get(wbs)) !== sumMap(series)) {
      throw new EngineError("invariant_leaf_total", `leaf curve does not match planned: ${wbs}`);
    }
  }
  for (const rolled of [rolledData, rolledCash, rolledRemaining]) {
    for (const series of rolled.values()) {
      for (const cents of series.values()) {
        if (cents < 0) throw new EngineError("invariant_negative", "negative cents in output");
      }
    }
  }
  let committedTotal = 0;
  for (const { paidCents, toPayCents } of committedByLeaf.values()) {
    committedTotal += paidCents + toPayCents;
  }
  const cashRoot = sumMap(rolledCash.get(input.root) ?? new Map());
  const remainingRoot = sumMap(rolledRemaining.get(input.root) ?? new Map());
  if (cashRoot !== committedTotal + remainingRoot) {
    throw new EngineError("invariant_cash_total", "cash projection does not reconcile", {
      cashRoot, committedTotal, remainingRoot,
    });
  }
};

// Roll-up de baixo para cima: cada folha e percorrida UMA vez e o que ela carrega
// e fundido nos acumuladores dela e dos ancestrais - O(folhas x profundidade),
// em vez de varrer todas as folhas por no.
const rollProvenance = (leafInfo, flagsByLeaf, nodes, exceedsNodes, root) => {
  const shareSets = new Map();
  const keySets = new Map();
  const flagSets = new Map();
  const bucket = (store, node) => {
    let set = store.get(node);
    if (!set) {
      set = new Set();
      store.set(node, set);
    }
    return set;
  };
  const chainOf = (wbs) => [wbs, ...ancestorsOf(wbs, root)];

  for (const [wbs, info] of leafInfo) {
    for (const node of chainOf(wbs)) {
      bucket(shareSets, node).add(info.share);
      if (info.ruleKeys.length > 0) {
        const keys = bucket(keySets, node);
        for (const key of info.ruleKeys) keys.add(key);
      }
    }
  }
  for (const [wbs, list] of flagsByLeaf) {
    if (list.length === 0) continue;
    for (const node of chainOf(wbs)) {
      const flags = bucket(flagSets, node);
      for (const flag of list) flags.add(flag);
    }
  }
  // o excedente de ajuste sobe do no do ajuste para os ancestrais
  for (const exceeding of exceedsNodes) {
    for (const node of chainOf(exceeding)) bucket(flagSets, node).add("exceeds_origin");
  }

  const result = new Map();
  for (const node of nodes) {
    const shares = shareSets.get(node);
    const share = !shares || shares.size === 0
      ? "none"
      : shares.size === 1 ? [...shares][0] : "partial";
    result.set(node, {
      share,
      ruleKeys: [...(keySets.get(node) ?? [])].sort().slice(0, MAX_PROVENANCE_KEYS),
      flags: [...(flagSets.get(node) ?? [])].sort(),
    });
  }
  return result;
};

// D25 (revisão de 2026-10-06 (3)): na origem orçamento, um serviço sem distribuição tem só
// o orçamento (`input.unscheduledLeaves`). Quando TODA fatia dele é vencida por uma regra de
// novo cronograma com início, a regra é o cronograma dele: o orçamento entra nas folhas
// planejadas no menor início e as regras o redistribuem a partir dali. Serviço governado só
// em parte, ou por regra sobre a referência, continua sem distribuição.
const scheduleUnscheduledBudget = (input, ruleIndex, ampliGroupByResource) => {
  const unscheduled = input.unscheduledLeaves;
  if (!(unscheduled instanceof Map) || unscheduled.size === 0) return input.plannedLeaves;
  const rowsByService = new Map();
  for (const row of input.composition ?? []) {
    if (!rowsByService.has(row.serviceWbs)) rowsByService.set(row.serviceWbs, []);
    rowsByService.get(row.serviceWbs).push(row);
  }
  const plannedLeaves = new Map(input.plannedLeaves);
  const governingRules = (serviceWbs) => {
    const nodeKeys = nodeKeysFor(serviceWbs, input.root);
    const rules = [];
    for (const unit of shareUnits(rowsByService.get(serviceWbs), serviceWbs)) {
      const rule = resolveRule({ unit, nodeKeys, ruleIndex, ampliGroupByResource });
      if (rule === null || rule.basis !== "new") return null;
      rules.push(rule);
    }
    return rules;
  };
  const earliest = (left, right) => (left === null || compareMonthKey(right, left) < 0 ? right : left);
  // 1ª passada: serviço com regra COM início entra no menor início
  const startless = [];
  const services = [...unscheduled].sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0));
  for (const [serviceWbs, cents] of services) {
    if (cents <= 0 || plannedLeaves.has(serviceWbs)) continue;
    const rules = governingRules(serviceWbs);
    if (rules === null) continue;
    let start = null;
    for (const rule of rules) if (rule.startMonth !== null) start = earliest(start, rule.startMonth);
    if (start === null) {
      startless.push([serviceWbs, cents, rules]);
      continue;
    }
    plannedLeaves.set(serviceWbs, new Map([[start, cents]]));
  }
  // 2ª passada (revisão de 2026-10-07): serviço só sob regras SEM início entra no mês em que
  // elas ancoram — o 1º mês que governam no resto da obra
  if (startless.length > 0) {
    const anchorOf = new Map();
    for (const [serviceWbs, series] of plannedLeaves) {
      let first = null;
      for (const [month, monthCents] of series) if (monthCents > 0) first = earliest(first, month);
      if (first === null) continue;
      const nodeKeys = nodeKeysFor(serviceWbs, input.root);
      for (const unit of shareUnits(rowsByService.get(serviceWbs), serviceWbs)) {
        const rule = resolveRule({ unit, nodeKeys, ruleIndex, ampliGroupByResource });
        if (rule !== null && rule.startMonth === null) {
          anchorOf.set(rule.key, earliest(anchorOf.get(rule.key) ?? null, first));
        }
      }
    }
    for (const [serviceWbs, cents, rules] of startless) {
      let start = null;
      for (const rule of rules) {
        const anchor = anchorOf.get(rule.key);
        if (anchor === undefined) {
          start = null;
          break;
        }
        start = earliest(start, anchor);
      }
      if (start !== null) plannedLeaves.set(serviceWbs, new Map([[start, cents]]));
    }
  }
  return plannedLeaves;
};

export const computeAmpliDisbursement = (input) => {
  validateInput(input);
  const ruleIndex = indexRules(input.rules);
  const ampliGroupByResource = indexAmpliGroups(input.groups, input.composition);
  // D25: o orçamento que uma regra agenda do zero entra nas folhas planejadas; a curva da
  // origem continua sendo só a da origem.
  const scheduledLeaves = scheduleUnscheduledBudget(input, ruleIndex, ampliGroupByResource);
  const effective = scheduledLeaves === input.plannedLeaves ? input : { ...input, plannedLeaves: scheduledLeaves };
  const { leafData, leafInfo, ruleHits } = redistributeLeaves(effective, ruleIndex, ampliGroupByResource);
  const leavesByNode = indexLeavesByNode([...effective.plannedLeaves.keys()], input.root);
  const { status: adjustmentsStatus, exceedsNodes } = applyAdjustments(
    leafData, input.adjustments, leavesByNode, input.root,
  );
  const {
    inScope: committedInScope, committedOutsideScope, committedOutsideScopeCents,
  } = scopeCommitted(input.committed, input.root);
  const {
    remainingLeaves, cashLeaves, flagsByLeaf, committedByLeaf,
  } = netLeaves(leafData, committedInScope, input.currentMonth);
  const rolledData = rollUpSeries(leafData, input.root);
  const rolledRemaining = rollUpSeries(remainingLeaves, input.root);
  const rolledCash = rollUpSeries(cashLeaves, input.root);
  verifyInvariants({
    input: effective, leafData, rolledData, rolledCash, rolledRemaining, committedByLeaf,
  });

  const nodeIds = new Set([
    ...rolledData.keys(), ...rolledCash.keys(), ...rolledRemaining.keys(), input.root,
  ]);
  // The ORIGIN's own curve (before any rule or adjustment), rolled to the root (design D15).
  const originSeries = new Map();
  for (const series of input.plannedLeaves.values()) {
    for (const [month, cents] of series) addInto(originSeries, month, cents);
  }
  const provenance = rollProvenance(leafInfo, flagsByLeaf, nodeIds, exceedsNodes, input.root);
  const nodes = new Map();
  for (const wbs of [...nodeIds].sort()) {
    nodes.set(wbs, {
      level: levelOf(wbs, input.root),
      data: rolledData.get(wbs) ?? new Map(),
      remaining: rolledRemaining.get(wbs) ?? new Map(),
      cashProjection: rolledCash.get(wbs) ?? new Map(),
      provenance: {
        share: provenance.get(wbs).share,
        ruleKeys: provenance.get(wbs).ruleKeys,
      },
      flags: provenance.get(wbs).flags,
    });
  }

  let scheduledCents = 0;
  let ruledCents = 0;
  let leavesRuled = 0;
  let leavesPartial = 0;
  let leavesFollowsBase = 0;
  for (const info of leafInfo.values()) {
    scheduledCents += info.plannedTotal;
    ruledCents += info.ruledForLeaf;
    if (info.plannedTotal > 0 && info.ruledForLeaf === info.plannedTotal) leavesRuled += 1;
    else if (info.ruledForLeaf > 0) leavesPartial += 1;
    else if (info.plannedTotal > 0) leavesFollowsBase += 1;
  }
  // D14. `reference` is what the work SHOULD have distributed (the ERP coverage total, the
  // budget...); it is optional because for an origin that is its own reference (previsto,
  // físico-financeiro) there is no gap by construction. What the origin did not distribute
  // is `undistributed`; the rest is split into "segue a origem" and "regra própria".
  const referenceCents = Math.max(scheduledCents, input.reference?.cents ?? 0);
  const referenceLeaves = Math.max(leafInfo.size, input.reference?.leaves ?? 0);
  const orphanRules = [...ruleIndex.values()]
    .filter((rule) => !ruleHits.has(rule.key))
    .map(({ ruleId, key }) => ({ ruleId, key }))
    .sort((left, right) => (left.key < right.key ? -1 : left.key > right.key ? 1 : 0));
  return {
    nodes,
    committedByLeaf,
    coverage: {
      scheduledCents,
      ruledCents,
      percentage: scheduledCents > 0 ? ruledCents / scheduledCents : null,
      followsBaseCents: scheduledCents - ruledCents,
      undistributedCents: referenceCents - scheduledCents,
      referenceCents,
      distributedPercentage: referenceCents > 0 ? scheduledCents / referenceCents : null,
      leavesTotal: leafInfo.size,
      leavesRuled,
      leavesPartial,
      leavesFollowsBase,
      leavesUndistributed: referenceLeaves - leafInfo.size,
      orphanRules,
      committedOutsideScope,
      committedOutsideScopeCents,
    },
    adjustmentsStatus,
    originSeries: sorted(originSeries),
  };
};

/**
 * Pré-visualização do editor: redistribui o valor global da série da origem do alvo pelo
 * perfil, a partir do início, com a mesma repartição por maior resto usada pelo motor. Não
 * faz netting nem ajuste — mostra "origem × desembolso resultante" para um alvo só. Num
 * alvo com vários serviços o motor reparte por serviço, então o arredondamento pode
 * diferir em centavos; os números exatos vêm da prévia de impacto do servidor.
 */
export const previewTargetCurve = ({
  plannedSeries = [], profile, startMonth = null, basis = "new", unscheduledCents = 0,
} = {}) => {
  let lines;
  let chosenStart;
  try {
    lines = validateRuleProfile(profile, basis);
    chosenStart = validateStartMonth(startMonth);
  } catch (error) {
    throw new EngineError("invalid_input", error.message, { errorCode: error.errorCode });
  }
  // Mesma semântica do motor: o valor GLOBAL do alvo (todos os meses da origem) é
  // redistribuído a partir da âncora — o início da regra ou, sem ele, o primeiro mês com
  // valor na origem.
  let total = 0;
  let firstMonth = null;
  for (const point of Array.isArray(plannedSeries) ? plannedSeries : []) {
    const competence = String(point?.competence ?? "");
    const cents = point?.cents;
    if (!MONTH.test(competence)) throw new EngineError("invalid_input", `invalid competence ${competence}`);
    if (!isCents(cents) || cents < 0) throw new EngineError("negative_planned", `invalid planned cents on ${competence}`);
    total += cents;
    if (cents > 0 && (firstMonth === null || compareMonthKey(competence, firstMonth) < 0)) firstMonth = competence;
  }
  // D25: o orçamento do alvo ainda sem distribuição entra no valor global do novo
  // cronograma, que só tem de onde partir com o início escolhido.
  if (lines !== null && basis !== "reference"
    && Number.isSafeInteger(unscheduledCents) && unscheduledCents > 0) {
    total += unscheduledCents;
  }
  const anchorMonth = chosenStart ?? firstMonth;
  if (total === 0 || anchorMonth === null) return [];
  const out = new Map();
  if (lines === null) {
    // D21: a referência deslocada do primeiro mês com valor até o início, mês a mês
    const distance = monthIndex(parseMonthKey(anchorMonth)) - monthIndex(parseMonthKey(firstMonth));
    for (const point of plannedSeries) {
      if (point.cents > 0) addInto(out, shiftMonth(point.competence, distance), point.cents);
    }
    return [...sorted(out)].map(([competence, cents]) => ({ competence, cents }));
  }
  const pieces = lines[0].weight !== undefined
    ? allocateProportional(total, lines.map((line) => line.weight))
    : allocateCents(total, lines.map((line) => line.percentBps));
  const offsets = createMonthOffsetsResolver()(lines, anchorMonth);
  lines.forEach((line, index) => {
    addInto(out, shiftMonth(anchorMonth, offsets[index]), pieces[index]);
  });
  return [...sorted(out)].map(([competence, cents]) => ({ competence, cents }));
};

/**
 * A competência em que cada linha do perfil cai, a partir do início (`anchorMonth`), na
 * mesma conversão do motor (dias corridos/úteis via calendar.js). Aceita a forma interna
 * do editor (`{ unit, offset }`) e a gravada (`{ offsetMonths }`). Linha incompleta ou
 * início inválido → `null` naquela posição: a tabela mostra "—" em vez de inventar mês.
 */
export const lineCompetences = (lines, anchorMonth) => (Array.isArray(lines) ? lines : []).map((line) => {
  if (!MONTH.test(String(anchorMonth ?? ""))) return null;
  const unit = line?.unit ?? "months";
  const offset = unit === "months" ? line?.offsetMonths ?? line?.offset : line?.offset;
  if (!Number.isSafeInteger(offset)) return null;
  const monthOffset = resolveLineMonthOffset(
    unit === "months" ? { unit, offsetMonths: offset } : { unit, offset },
    anchorMonth,
  );
  return shiftMonth(anchorMonth, monthOffset);
});
